import { Injectable, OnDestroy, Signal, computed, inject, signal } from '@angular/core';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../supabase/supabase.client';
import { ErrorLoggerService } from '../error/error-logger.service';

/**
 * Global, DB-driven feature flag store.
 *
 * Flags live in the `public.feature_flags` table (one row per flag, key + enabled).
 * All authenticated users can SELECT; only the Supabase service role can mutate,
 * which means day-to-day toggling is a SQL `UPDATE feature_flags SET enabled = ...`.
 *
 * The Realtime subscription means a toggle propagates to live sessions within a
 * second — no page refresh needed.
 *
 * Fail-closed by design: if the initial load fails or a key is unknown,
 * `isEnabled()` returns false. Features are off until proven on.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagService implements OnDestroy {
  private readonly errorLogger = inject(ErrorLoggerService);

  private readonly _flags = signal<Record<string, boolean>>({});
  readonly flags = this._flags.asReadonly();
  readonly loaded = signal(false);

  private channel: RealtimeChannel | null = null;

  isEnabled(key: string): boolean {
    return this._flags()[key] === true;
  }

  isEnabledSignal(key: string): Signal<boolean> {
    return computed(() => this._flags()[key] === true);
  }

  async loadAll(): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('key, enabled');
      if (error) throw error;
      const map: Record<string, boolean> = {};
      for (const row of data ?? []) {
        map[row.key as string] = row.enabled === true;
      }
      this._flags.set(map);
      this.loaded.set(true);
    } catch (error) {
      this.errorLogger.log(error, undefined, { context: 'FeatureFlagService.loadAll' });
      // Leave _flags as-is so a transient failure doesn't immediately drop
      // already-loaded flags. First-load failures leave it empty → fail-closed.
    }
  }

  subscribeToChanges(): void {
    if (this.channel) return;
    this.channel = supabase
      .channel('feature_flags_changes')
      .on(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        'postgres_changes' as any,
        { event: '*', schema: 'public', table: 'feature_flags' },
        () => {
          void this.loadAll();
        },
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    if (this.channel) {
      void supabase.removeChannel(this.channel);
      this.channel = null;
    }
  }
}
